// Torque-Power-RPM Calculator tool: mode/unit metadata + client-side field validation,
// used for instant inline feedback as the user types. The actual reverse-solve math and
// the authoritative (server-side) validation live in the main backend — see
// src/lib/toolsApi.js (torquePowerRpmToolApi) and backend/tools/torque_power_rpm_tool.py.
// Mirrors backend/core/lookup/torque_power_rpm_specs.py exactly.

// One shared field set covers all 3 modes — switching modes keeps whatever the user
// already typed in Power/RPM/Torque rather than resetting them, since all 3 quantities
// are simultaneously related by the same triangle: Torque (Nm) = Power (kW) x 9550 / RPM.
export const DEFAULT_INPUTS = {
  powerValue: 100,
  powerUnit: 'kw',
  rpm: 3000,
  torqueValue: 300,
  torqueUnit: 'nm',
};

export const MODES = [
  {
    id: 'torque',
    label: 'Torque from Power / RPM',
    shortLabel: 'Torque',
    description: 'Calculate torque when Power and RPM are known.',
    formula: 'Torque (Nm) = Power (kW) × 9550 / RPM',
  },
  {
    id: 'rpm',
    label: 'RPM from Power / Torque',
    shortLabel: 'RPM',
    description: 'Calculate rotational speed when Power and Torque are known.',
    formula: 'RPM = Power (kW) × 9550 / Torque (Nm)',
  },
  {
    id: 'power',
    label: 'Power from Torque / RPM',
    shortLabel: 'Power',
    description: 'Calculate power when Torque and RPM are known.',
    formula: 'Power (kW) = Torque (Nm) × RPM / 9550',
  },
];

// 1 HP (mechanical/SAE) = 745.699872 W exactly, matching kwHpTools.js. 1 PS (metric
// horsepower) = 735.49875 W exactly.
export const POWER_UNITS = [
  { value: 'kw', label: 'kW', name: 'Kilowatts' },
  { value: 'w', label: 'W', name: 'Watts' },
  { value: 'hp', label: 'HP', name: 'Horsepower (mechanical)' },
  { value: 'ps', label: 'PS', name: 'Horsepower (metric/PS)' },
];
export const POWER_UNIT_VALUES = POWER_UNITS.map((u) => u.value);

// lb-ft factor matches torqueTools.js exactly.
export const TORQUE_UNITS = [
  { value: 'nm', label: 'N·m', name: 'Newton-Meters' },
  { value: 'lbft', label: 'lb-ft', name: 'Pound-Feet' },
];
export const TORQUE_UNIT_VALUES = TORQUE_UNITS.map((u) => u.value);

// Power is a non-negative magnitude (0 is a valid, if degenerate, input). RPM and
// Torque must be strictly positive — RPM = 0 or Torque = 0 make the reverse-solve
// formulas divide by zero, and both are explicitly invalid per the spec.
export const POWER_SPEC = { key: 'powerValue', label: 'Power', unit: '', group: 'input', min: 0, max: 1000000000, strictlyPositive: false };
export const RPM_SPEC = { key: 'rpm', label: 'RPM', unit: 'rpm', group: 'input', min: 0, max: 200000, strictlyPositive: true };
export const TORQUE_SPEC = { key: 'torqueValue', label: 'Torque', unit: '', group: 'input', min: 0, max: 1000000000, strictlyPositive: true };

// Which fields are required/validated for each of the 3 calculation modes.
export const MODE_FIELD_SPECS = {
  torque: [POWER_SPEC, RPM_SPEC],
  rpm: [POWER_SPEC, TORQUE_SPEC],
  power: [TORQUE_SPEC, RPM_SPEC],
};

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return `${spec.label} must be greater than 0.`;
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
